import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import {
  getClinicInformation,
  profilePostAction,
  updateClinicLogo,
} from "../../actions/profileAction";
import FormikControl from "../../components/WraapperComponent/FormikControl";
import { states } from "../../constants/states";
import { ERROR } from "../../constants/types/alertType";
import { getUserDetail } from "../../utils/helper";
import { SketchPicker } from "react-color";
import ColorPicker from "./ColorPicker";
import ImageCropper from "../../components/ImageCropper";

const ClinicInformationPopup = ({ clinicInfo, setValue, closePopup }) => {
  const dispatch = useDispatch();
  const [uploadFile, getuploadFile] = useState(null);
  const [showImageCropper, setShowImageCropper] = useState(false);

  const initialValues = {
    clinicname: clinicInfo?.clinicname,
    website: clinicInfo?.website ?? null,
    email: clinicInfo?.email,
    address_1: clinicInfo?.address_1,
    address_2: clinicInfo?.address_2 ?? null,
    city: clinicInfo?.city,
    state: clinicInfo?.state,
    phone_1: clinicInfo?.phone_1,
    phone_2: clinicInfo?.phone_2 ?? null,
    zip: clinicInfo?.zip,
    hexValue: clinicInfo?.color_value ?? "#575757",
    hexHeading: clinicInfo?.color_headings ?? "#575757",
    file: clinicInfo?.file,
  };

  const validationSchema = Yup.object({
    clinicname: Yup.string().nullable().required("This field is required"),
    website: Yup.string()
      .nullable()
      .matches(
        /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/,
        "Url is not valid"
      ),
    email: Yup.string().nullable().email().required("This field is required"),
    phone_1: Yup.string()
      .nullable()
      .min(10, "Phone number must be 10 digits long")
      .max(10, "Phone number must be 10 digits long")
      .matches(
        /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
        "Phone number is not valid"
      )
      .required("This field is required"),
    address_1: Yup.string().nullable().required("This field is required"),
    address_2: Yup.string().nullable(),
    city: Yup.string().nullable().required("This field is required"),
    state: Yup.string().nullable().required("This field is required"),
    file: Yup.mixed(),
    hexValue: Yup.string().nullable(),

    hexHeading: Yup.string().nullable(),

    zip: Yup.string()
      .nullable()
      .max(5)
      .min(5)
      .matches(/^\d{5}$/, "Zip code is not valid")
      .required("This field is required"),
  });
  const greyScaleValue = (hex) => {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    result = {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    };
    return 0.2126 * result.r + 0.7152 * result.g + 0.0722 * result.b;
  };
  const onSubmit = (values) => {
    if (
      greyScaleValue(values.hexHeading) > 230 ||
      greyScaleValue(values.hexValue) > 230
    ) {
      dispatch({
        type: ERROR,
        payload: "please do not utilize colors value proximate to white",
      });
      return;
    }
    let actionName = clinicInfo?.clinicId ? "userEditClinic" : "userAddClinic";
    const formData = new FormData();
    formData.append("token", getUserDetail()?.token);
    formData.append("clinic_name", values.clinicname);
    formData.append("clinic_url", values.website);
    formData.append("clinic_email", values.email);
    formData.append("clinic_address_1", values.address_1);
    formData.append("clinic_address_2", values.address_2 || "");
    formData.append("clinic_city", values.city);
    formData.append("clinic_state", values.state);
    formData.append("clinic_phone", values.phone_1);
    formData.append("clinic_phone_2", values?.phone_2 || "");
    formData.append("clinic_zip", values.zip);
    formData.append("clinic_id", clinicInfo?.clinicId || "");
    formData.append("clinic_label_value_color", values.hexValue.substring(1));
    formData.append(
      "clinic_label_heading_color",
      values.hexHeading.substring(1)
    );
    formData.append("action", actionName);
    formData.append("clinic_logo", uploadFile);

    dispatch(
      updateClinicLogo(formData, () => {
        dispatch(getClinicInformation());
      })
    );
    closePopup();
  };
  return (
    <>
      <Modal size="lg" show={true} backdrop="static">
        <Modal.Header>
          <div className="modal-title">
            <h5 className="profile-title">clinic information</h5>
            <div className="close-btn">
              <FontAwesomeIcon icon={faTimesCircle} onClick={closePopup} />
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                errors,
                touched,
                handleChange,
                values,
                handleBlur,
                setFieldValue,
              }) => {
                return (
                  <Form>
                    <div className="row">
                      <div className="col-sm-12">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label="* Clinic Name"
                          name="clinicname"
                          handleBlur={handleBlur}
                          hasError={errors.clinicname && touched.clinicname}
                        />
                      </div>
                      <div className="col-sm-6">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label="Website"
                          name="website"
                          handleBlur={handleBlur}
                          hasError={errors.website && touched.website}
                        />
                      </div>
                      <div className="col-sm-6">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label="* Email"
                          handleBlur={handleBlur}
                          name="email"
                          hasError={errors.email && touched.email}
                        />
                      </div>
                      <div className="col-sm-8">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label="* Street"
                          handleBlur={handleBlur}
                          name="address_1"
                          hasError={errors.address_1 && touched.address_1}
                        />
                      </div>
                      <div className="col-sm-4">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label="Apt"
                          handleBlur={handleBlur}
                          name="address_2"
                          hasError={errors.address_2 && touched.address_2}
                        />
                      </div>
                      <div className="col-sm-4">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label="* City"
                          handleBlur={handleBlur}
                          name="city"
                          hasError={errors.city && touched.city}
                        />
                      </div>
                      <div className="col-sm-4">
                        <FormikControl
                          control="dropdown"
                          label="State"
                          name="state"
                          handleBlur={handleBlur}
                          hasError={errors.state && touched.state}
                          options={states}
                        />
                      </div>
                      <div className="col-sm-4">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label="* Zip code"
                          name="zip"
                          handleBlur={handleBlur}
                          maxLength={5}
                          hasError={errors.zip && touched.zip}
                        />
                      </div>
                      <div className="col-sm-6">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label="* Phone"
                          name="phone_1"
                          handleBlur={handleBlur}
                          hasError={errors.phone_1 && touched.phone_1}
                        />
                      </div>
                      <div className="col-sm-6">
                        <FormikControl
                          control="forminput"
                          type="text"
                          label="Secondary Phone"
                          name="phone_2"
                          handleBlur={handleBlur}
                          hasError={errors.phone_2 && touched.phone_2}
                        />
                      </div>
                      <div className="col-sm-6">
                        <div
                          className={
                            errors.hexHeading && touched.hexHeading
                              ? "form-group has-error star"
                              : "form-group star"
                          }
                        >
                          <span className="input-focus"></span>
                          <label>Rx Headings Color</label>
                          <div className="input password-field">
                            <ColorPicker
                              setFieldValue={setFieldValue}
                              fieldName="hexHeading"
                              color={values.hexHeading}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div
                          className={
                            errors.hexValue && touched.hexValue
                              ? "form-group has-error star"
                              : "form-group star"
                          }
                        >
                          <span className="input-focus"></span>
                          <label>Rx Values Color</label>
                          <div className="input password-field">
                            <ColorPicker
                              setFieldValue={setFieldValue}
                              fieldName="hexValue"
                              color={values.hexValue}
                            />
                          </div>
                        </div>
                      </div>{" "}
                      <div className="col-sm-6">
                        <p
                          className=" mt-2 mb-2 test-label"
                          onClick={() => setShowImageCropper(true)}
                        >
                          Upload Clinic Logo
                        </p>
                        {uploadFile ? <p>logo.png</p> : ""}
                      </div>
                    </div>
                    <div className="submit-section mt-2">
                      <button className="btn-orange" type="submit">
                        Save
                      </button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </Modal.Body>
      </Modal>
      {showImageCropper && (
        <ImageCropper
          closePopup={() => setShowImageCropper(false)}
          getuploadFile={getuploadFile}
          clinicId={clinicInfo && clinicInfo.clinicId}
          setValue={setValue}
        />
      )}
    </>
  );
};
export default ClinicInformationPopup;
